import router from '@/router';
import { defineStore } from 'pinia';
import { login, register, passwordReset, passwordSelect, passwordChange, updateUser } from '@/services/ppApi';
// import { useLanguageStore } from '@/stores/utils/language';
import { useAccountStore } from '@/stores/account';
import { gaEvent } from '@/utils/analytics/prospectpro';

export const USER_SETTINGS_KEYS = {
  order:           'pp_order_settings',
  prospects:       'pp_prospects_settings',
  logs:            'pp_logs_settings',
  companies:       'pp_companies_settings',
  prospectDetails: 'pp_prospect_details_settings',
  searchHistory:   'pp_search_history',
};

export const useUserStore = defineStore('user', {
  state: () => ({
    token: localStorage.getItem('pp_auth_token') || null,
    redirectAfterLogin: null,
    user: JSON.parse(localStorage.getItem('pp_user')) || null,
    error: null,
    loading: false,
  }),
  getters: {
    isAuthenticated: (state) => !!state.token,
    hasUserDetails: (state) => !!state.user,
    isPartnerAdmin: (state) => !!state.user?.partner_role > 0 && state.user.partner_role < 11,
    partnerRole: (state) => state.user?.partner_role || 100,
    isPartnerGod: (state) => !!state.user?.partner_role > 0 && state.user.partner_role < 5
  },
  actions: {
    setToken(token) {
      this.token = token;
      if (token) {
        localStorage.setItem('pp_auth_token', token);
      } else {
        localStorage.removeItem('pp_auth_token');
      }
    },
    setUser(updatedUser) {
        this.user = { ...this.user, ...updatedUser };
        if(updatedUser) {
            localStorage.setItem('pp_user', JSON.stringify(this.user));
        } else {
            localStorage.removeItem('pp_user');
        }
    },
    resetUser() {
        this.setToken(null);
        this.setUser(null);
        Object.values(USER_SETTINGS_KEYS).forEach(key => this.deleteAllSettings(key));
        this.$reset();
    },
    reauthenticate() {
        this.resetUser();
        this.setRedirectAfterLogin(router.currentRoute.value.name || null);
        router.push({ name:'login'});
    },
    async login(email, password) {
        this.loading = true;
        this.error = null;

      try {
        const response = await login(email, password);
        this.setToken(response.data.token);
        this.setUser({
            email,
            name: response.data.name || null,
            uid: response.data.uid || null
        });

        // const languageStore = useLanguageStore();
        // const userPreferredLanguage = response.data.language;
        // if (userPreferredLanguage) {
        //   await languageStore.setLocale(userPreferredLanguage);
        // }

        if (response.data.default_account_id) {
            const accountStore = useAccountStore();
            const account = {
                account_id: response.data.default_account_id,
            };
            accountStore.setAccount(account);
        }

        if (response.data.partner_role && response.data.partner_role > 0) {
            this.setUser({
                partner_role: response.data.partner_role
            });
        }

        gaEvent('user_signin');
        return true;
      } catch (error) {
        this.error = error?.response?.data ? error.response.data : error;
        gaEvent('user_signin', false);
        return false;
      } finally {
        this.loading = false;
      }
    },
    async register(name, url, email, password) {
        this.loading = true;
        this.error = null;

        try {
            const response = await register(name, url, email, password);
            if (response.status === 200) {
                const loginSuccess = await this.login(email, password);
                gaEvent('user_registration');
                return loginSuccess;
            } else {
                gaEvent('user_registration', false);
                return false;
            }
        } catch (error) {
            this.error = error?.response?.data ? error.response.data : error;
            gaEvent('user_registration', false);
            return false;
        } finally {
            this.loading = false;
        }
    },
    async passwordChange(password, passwordNew, passwordConfirm) {
        this.loading = true;
        this.error = null;

        try {
          const response = await passwordChange(password, passwordNew, passwordConfirm);
          this.setToken(response.data.token);
          return true;
        } catch (error) {
            this.error = error?.response?.data ? error.response.data : error;
          return false;
        } finally {
          this.loading = false;
        }
    },
    async passwordReset(email) {
        this.loading = true;
        this.error = null;

      try {
        await passwordReset(email);
        gaEvent('password_reset');
        return true;
      } catch (error) {
        this.error = error?.response?.data ? error.response.data : error;
        gaEvent('password_reset', false);
        return false;
      } finally {
        this.loading = false;
      }
    },
    async passwordSelect(password, r) {
        this.loading = true;
        this.error = null;

      try {
        await passwordSelect(password, r);
        gaEvent('password_select');
        return true;
      } catch (error) {
        this.error = error?.response?.data ? error.response.data : error;
        gaEvent('password_select', false);
        return false;
      } finally {
        this.loading = false;
      }
    },
    async updatePersonalia(user) {
        this.loading = true;
        this.error = null;

        try {
            const response = await updateUser(user);
            this.setUser(response.data.users[0]);
            return true;
        } catch (error) {
            this.error = error?.response?.data ? error.response.data : error;
            return false;
        } finally {
            this.loading = false;
        }
    },
    logout() {
        gaEvent('user_signout');
        this.resetUser();
        const accountStore = useAccountStore();
        if(accountStore.hasAccountId) {
            accountStore.resetAccount();
        }
        window.location.reload();
    },
    setRedirectAfterLogin(path = null) {
        if(path) {
            this.redirectAfterLogin = path;
        } else {
            this.redirectAfterLogin = null;
        }
    },
    redirectUserAfterLogin() {
      if (this.redirectAfterLogin) {
        router.push(this.redirectAfterLogin);
        this.redirectAfterLogin = null;
      } else {
        router.push({name: process.env.VUE_APP_DEFAULT_ROUTE});
      }
    },
    deleteAllSettings(key) {
        localStorage.removeItem(key);
    },
    getSettings(key, account_id = null) {
        if (!account_id) return false;
        const all = JSON.parse(localStorage.getItem(key)) || null;
        return all?.[account_id] ?? false;
    },
    saveSettings(key, account_id = null, settings = null) {
        if (!account_id) return false;
        const all = JSON.parse(localStorage.getItem(key)) || {};
        all[account_id] = settings || null;
        localStorage.setItem(key, JSON.stringify(all));
    },
  }
});